import {
  BATTLESHOCK_PHASE,
  CHARGE_PHASE,
  COMBAT_PHASE,
  DURING_GAME,
  DURING_SETUP,
  END_OF_HERO_PHASE,
  END_OF_MOVEMENT_PHASE,
  HERO_PHASE,
  MOVEMENT_PHASE,
  SAVES_PHASE,
  SHOOTING_PHASE,
  START_OF_MOVEMENT_PHASE,
  START_OF_SETUP,
  TURN_ONE_START_OF_HERO_PHASE,
  WARDS_PHASE,
  WOUND_ALLOCATION_PHASE,
} from 'types/phases'

// This file is useful when storing abilities for units that we'd like to re-use
const GenericEffects = {
  // e.g. all mercenaries share the "Disruptive Presence" effect, so we'll store it here.
  DisruptivePresence: {
    name: `Disruptive Presence`,
    desc: `If your army includes any MERCENARY units, at the start of your hero phase in the first battle round, you do not receive 1 command point for your general being on the battlefield.`,
    when: [TURN_ONE_START_OF_HERO_PHASE],
    shared: true,
  },
  // We re-use this Timber! a lot as well, so we stuck it here!
  Gargant: [
    {
      name: `Timber!`,
      desc: `If this model is slain, before removing the model from the battlefield, the players must roll off. The winner picks a point on the battlefield 3" from the slain model. Each unit within 2" of that point that is not a Gargant or a Mega-Gargant suffers D3 mortal wounds. The slain model is then removed from the battlefield.`,
      when: [WOUND_ALLOCATION_PHASE],
      shared: true,
    },
  ],
  // You get the point
  Terrorgheist: [
    {
      name: `Death Shriek`,
      desc: `Do not use the attack sequence for an attack made with this unit's Death Shriek. Instead, roll a dice and add the Death Shriek value shown on this unit's damage table. If the total is higher than the target unit's Bravery characteristic, the target unit suffers a number of mortal wounds equal to the difference between its Bravery characteristic and the total.`,
      when: [SHOOTING_PHASE],
      shared: true,
    },
    {
      name: `Gaping Maw`,
      desc: `If the unmodified hit roll for an attack made with this unit's Fanged Maw is 6, the target suffers 6 mortal wounds and the attack sequence ends (do not make a wound or save roll).`,
      when: [COMBAT_PHASE],
      shared: true,
    },
    {
      name: `Infested`,
      desc: `If this unit is destroyed, before it is removed from play, each enemy unit within 3" of this unit suffers D3 mortal wounds.`,
      when: [WOUND_ALLOCATION_PHASE],
      shared: true,
    },
  ],
  ZombieDragon: [
    {
      name: `Terror`,
      desc: `Enemy units cannot receive the Inspiring Presence command while they are within 3" of any friendly units with this ability.`,
      when: [BATTLESHOCK_PHASE],
      shared: true,
    },
    {
      name: `Loathsome Descent`,
      desc: `During deployment, instead of setting up this unit on the battlefield, you can place it to one side and say that it is circling high above as a reserve unit.`,
      when: [DURING_SETUP],
      shared: true,
    },
    {
      name: `Loathsome Descent`,
      desc: `If you set this unit up in reserve, at the end of your movement phase, you can set up this unit on the battlefield more than 9" from all enemy units.`,
      when: [END_OF_MOVEMENT_PHASE],
      shared: true,
    },
  ],
  CrewedWarMachine: (name: 'Duardin Artillery' | 'Crewed Artillery' | 'Crewed War Machine') => [
    {
      name,
      desc: `This unit can only move if its Crew are within 1" of the war machine at the start of the movement phase.`,
      when: [START_OF_MOVEMENT_PHASE],
      shared: true,
    },
    {
      name,
      desc: `If this war machine's Crew are within 1" of the war machine in the shooting phase, they can fire the war machine.`,
      when: [SHOOTING_PHASE],
      shared: true,
    },
    {
      name,
      desc: `The war machine cannot make charge moves.`,
      when: [CHARGE_PHASE],
      shared: true,
    },
    {
      name,
      desc: `The war machine does not need to take battleshock tests and is unaffected by any attack or ability that uses Bravery.`,
      when: [BATTLESHOCK_PHASE],
      shared: true,
    },
    {
      name,
      desc: `The Crew are in cover while they are within 1" of their war machine.`,
      when: [COMBAT_PHASE, SHOOTING_PHASE],
      shared: true,
    },
  ],
  AelvenShield: {
    name: `Aelven Shield`,
    desc: `You can reroll save rolls of 1 for a unit with Aelven Shields. You can reroll failed save rolls of 1 or 2 for this unit in the shooting phase.`,
    when: [SAVES_PHASE],
    shared: true,
  },
  Dragonfire: {
    name: `Dragonfire`,
    desc: `A Dragon can unleash a blast of Dragonfire in your shooting phase. When it does so, pick a visible unit within 12" and roll a D6; on a 1 or 2 that unit suffers a mortal wound, on a 3 or 4 that unit suffers D3 mortal wounds, and on a 5 or 6 that unit suffers D6 mortal wounds.`,
    when: [SHOOTING_PHASE],
    shared: true,
  },
  Terror: {
    name: `Terror`,
    desc: `Subtract 1 from the Bravery characteristic of enemy units if they are within 3" of any friendly units with this ability.`,
    when: [BATTLESHOCK_PHASE],
    shared: true,
  },
  Impassable: {
    name: `Impassable`,
    desc: `You cannot move a model over this terrain feature unless it can fly, and you cannot move a model onto this terrain feature or set up a model on this terrain feature (even if it can fly).`,
    when: [DURING_GAME],
    shared: true,
  },
  Defensible: {
    name: `Defensible`,
    desc: `This terrain feature is a defensible terrain feature that can be garrisoned by 1 HERO with a Wounds characteristic of 8 or less.`,
    when: [DURING_GAME],
    shared: true,
  },
  FactionTerrainSetup: {
    name: `Setup`,
    desc: `After territories are determined, you can set up this faction terrain feature wholly within your territory and more than 3" from all objectives and other terrain features. If both players can set up faction terrain features at the same time, they must roll off and the winner chooses who sets up their faction terrain features first.`,
    when: [START_OF_SETUP],
    shared: true,
  },
  FactionTerrainSetup_Removable: {
    name: `Setup`,
    desc: `After territories are determined, you can set up this faction terrain feature wholly within your territory and more than 3" from all objectives and other terrain features. If these restrictions mean you cannot set up this faction terrain feature, you can remove 1 terrain feature that is wholly or partially within your territory and attempt to set up this faction terrain feature again. If it is still impossible to set up this faction terrain feature, then it is not used. If both players can set up faction terrain features at the same time, they must roll off and the winner chooses who sets up their faction terrain features first.`,
    when: [START_OF_SETUP],
    shared: true,
  },
  Predatory: {
    Six_Inches: {
      name: `Predatory`,
      desc: `This endless spell is a predatory endless spell. It can be moved up to 8" and can fly.`,
      when: [END_OF_HERO_PHASE],
      shared: true,
    },
    Eight_Inches: {
      name: `Predatory`,
      desc: `This endless spell is a predatory endless spell. It can be moved up to 8" and can fly.`,
      when: [END_OF_HERO_PHASE],
      shared: true,
    },
    Nine_Inches: {
      name: `Predatory`,
      desc: `This endless spell is a predatory endless spell. It can be moved up to 9" and can fly.`,
      when: [END_OF_HERO_PHASE],
      shared: true,
    },
    Twelve_Inches: {
      name: `Predatory`,
      desc: `This endless spell is a predatory endless spell. It can be moved up to 12" and can fly.`,
      when: [END_OF_HERO_PHASE],
      shared: true,
    },
    Two_D6: {
      name: `Predatory`,
      desc: `This endless spell is a predatory endless spell. It can be moved up to 2D6" and can fly.`,
      when: [END_OF_HERO_PHASE],
      shared: true,
    },
    Three_D6: {
      name: `Predatory`,
      desc: `This endless spell is a predatory endless spell. It can be moved up to 3D6" and can fly.`,
      when: [END_OF_HERO_PHASE],
      shared: true,
    },
  },
  Linked: {
    name: `Linked`,
    desc: `If the model that summoned this endless spell is removed from play, then this endless spell is removed from play.`,
    when: [WOUND_ALLOCATION_PHASE],
    shared: true,
  },
  Elite: {
    name: `Elite`,
    desc: `Models in this unit can issue commands to their own unit.`,
    when: [DURING_GAME],
    shared: true,
  },
  Bonded: [
    {
      name: `Bonded`,
      desc: `This endless spell is bonded to the model that summoned it. A bonded endless spell is always controlled by the model to which it is bonded. A model cannot be bonded to more than one endless spell at the same time and cannot attempt to summon other endless spells while it is bonded. If the model that summoned this endless spell is removed from play, then this endless spell is removed from play.`,
      when: [DURING_GAME],
      shared: true,
    },
    {
      name: `Bonded`,
      desc: `If the model that summoned this endless spell is removed from play, then this endless spell is removed from play.`,
      when: [WOUND_ALLOCATION_PHASE],
      shared: true,
    },
  ],
  InfantryHornblowerEffect: {
    name: `Hornblower`,
    desc: `Reroll dice rolls of 1 when determining how far this unit can run or charge while it includes any Hornblowers.`,
    when: [MOVEMENT_PHASE, CHARGE_PHASE],
    shared: true,
  },
  ClanBannerEffect: {
    name: `Clan Banner`,
    desc: `If you fail a battleshock test for a unit that has any Clan Banners, halve the number of models that flee (rounding up).`,
    when: [BATTLESHOCK_PHASE],
    shared: true,
  },
  ArcaniteShieldEffect: {
    name: `Arcanite Shield`,
    desc: `A model that has an Arcanite Shield has a ward of 6+.`,
    when: [WARDS_PHASE],
    shared: true,
  },
  WizardOneSpellEffect: {
    name: `Wizard`,
    desc: `This unit can attempt to cast 1 spell in your hero phase and attempt to unbind 1 spell in the enemy hero phase.`,
    when: [HERO_PHASE],
    shared: true,
  },
  WizardTwoSpellsEffect: {
    name: `Wizard`,
    desc: `This unit can attempt to cast 2 spells in your hero phase and attempt to unbind 2 spells in the enemy hero phase.`,
    when: [HERO_PHASE],
    shared: true,
  },
}

export default GenericEffects
